import { useState } from 'react'
import { ChevronRight } from 'lucide-react'

import type { CustomAgentInvocation } from '@axilon-apps/domains-ontology-ui-state'

import { SidebarToolCallList } from '../../components/ToolTrace/ToolCallRow'
import { useDebugMode } from '../../lib/debug-mode'
import { cn } from '../../lib/utils'
import { invocationsToToolCallEntries } from './follow-up-invocations'
import { formatAbsoluteTime, formatCompactTime, formatRunDuration, sameSecond } from './run-timing'
import { groupRunInvocations, summarizeInvocationWaves } from './run-invocation-waves'

interface Props {
  createdAt: string
  startedAt?: string | null
  finishedAt?: string | null
  invocations: CustomAgentInvocation[]
  now: number
}

export function RunDetailDisclosure({ createdAt, startedAt, finishedAt, invocations, now }: Props) {
  const debugMode = useDebugMode()
  const [open, setOpen] = useState(false)

  const duration = formatRunDuration(startedAt, finishedAt, now)
  const hideStarted = sameSecond(createdAt, startedAt)
  const waves = groupRunInvocations(invocations)
  const waveSummary = summarizeInvocationWaves(waves)
  const summaryParts = [
    ...(debugMode
      ? [
          `${waveSummary.calls} tool call${waveSummary.calls === 1 ? '' : 's'}`,
          waveSummary.parallelTurns > 0
            ? `${waveSummary.parallelTurns} parallel turn${waveSummary.parallelTurns === 1 ? '' : 's'}`
            : null,
          waveSummary.cypherQueries > 0
            ? `${waveSummary.cypherQueries} Cypher ${
                waveSummary.cypherQueries === 1 ? 'query' : 'queries'
              }`
            : null
        ]
      : []),
    duration === '—' ? null : duration
  ]
  const summary = summaryParts.filter((part): part is string => part !== null).join(' · ')

  const toolCalls = invocationsToToolCallEntries(invocations, startedAt ?? createdAt)

  const stamps: { label: string; value: string; iso: string | null | undefined }[] = [
    { label: 'Created', value: formatCompactTime(createdAt), iso: createdAt },
    ...(hideStarted
      ? []
      : [{ label: 'Started', value: formatCompactTime(startedAt), iso: startedAt }]),
    { label: 'Finished', value: finishedAt ? formatCompactTime(finishedAt) : '—', iso: finishedAt },
    { label: 'Duration', value: duration, iso: null }
  ]

  if (!debugMode) return null

  return (
    <section data-open={open} className="rounded-lg border border-line bg-bg-elev">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-left"
      >
        <ChevronRight
          className={cn(
            'size-3.5 shrink-0 text-ink-4 transition-transform duration-200 ease-out',
            open && 'rotate-90'
          )}
          aria-hidden
        />
        <span className="text-13 font-medium text-ink-2">Run detail</span>
        <span className="text-12 text-ink-4">{summary}</span>
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-line px-4 py-3">
            <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-12 sm:grid-cols-4">
              {stamps.map((stamp) => (
                <div key={stamp.label}>
                  <div className="text-10 uppercase tracking-wide text-ink-4">{stamp.label}</div>
                  <div
                    className="tabular-nums text-ink-2"
                    title={stamp.iso ? formatAbsoluteTime(stamp.iso) : undefined}
                  >
                    {stamp.value}
                  </div>
                </div>
              ))}
            </div>

            {debugMode && toolCalls.length > 0 ? <SidebarToolCallList calls={toolCalls} /> : null}
          </div>
        </div>
      </div>
    </section>
  )
}
